import React, { useCallback, useMemo, useState, useEffect } from "react";
import Moment from "react-moment";
import { motion } from "framer-motion";
import axios from "../axios/axios";

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import {Helmet} from "react-helmet-async";
import {useAuth} from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserTie, faCrown, faDragon, faEye, faSkull, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import "../styles/UserDashboard.scss";

import { toast } from "react-toastify";

import UserCard from "./UserCard";

//Socketio
import io from "socket.io-client";

const token = localStorage.getItem("token");
const isDev = process.env.REACT_APP_DEV === "true";

const socket = io.connect(isDev ? "http://localhost:52678" : "https://anvil.team", {
	autoConnect: true,
	transports: ["websocket"],
	//auth: { token },
});


const configKeyToLabel = {
    autoStrafe: "Auto Strafe",
    autoFPS: "Auto FPS",
    anvilEngine: "Optimizations",
    autoRPG: "Auto RPG Lookdown",
    edgeJump: "Auto Edge Jump",
    //eleBot: "Elebot",
    fpsWheel2D: "FPS Wheel (2D)",
    fpsWheel3D: "FPS Wheel (3D)",
    worldAxisCardinalLines: "90 Degree Lines",
    worldAxisDiagonalLines: "45 Degree Lines",
    accelerationVector: "Acceleration Vector",
    locationMarker: "Location Marker",
    showBrushCollision: "Brush Collision",
    showTerrainCollision: "Terrain Collision",
    usingCustomSkybox: "Using custom skybox",
    mirrorMode: "Using Mirror Mode"
};


function UserDashboard() {

	const navigate = useNavigate();
	const auth = useAuth();
    const [isLoading, setIsLoading] = useState(true);
	const [users, setUsers] = useState([]);

    const [showModal, toggleModal] = useState(false);
    const [canReport, toggleCanReport] = useState(false);
    const [canRequestConfig, toggleCanRequestConfig] = useState(true);

    const handleClose = () => {
        toggleModal(false);
        toggleCanReport(false);
    }

    const handleShow = () => toggleModal(true);

    const [userConfig, setUserConfig] = useState({});

    useEffect(() => {

        socket.removeAllListeners();
    
            socket.on("connect", () => {
            console.log("Connected socket!");
        });
    
        socket.on("connect_error", error => {
            //localStorage.removeItem("token");
            //navigate("/");
            //toast.error("Server connection rejected");
            //socket.disconnect();
            console.log(error);
        });

        // socket.on("current-users", onlineUsers => {
        //     //console.log(onlineUsers);
        //     //console.log(users);
        //     const newUsers = users.map(user => {
        //         if (onlineUsers.find(onlineUser => user.name === onlineUser)) {
        //             user.isOnline = true;
        //         }
        //         return user;
        //     });
        //     //console.log("would set users to", newUsers);
        //     setUsers(newUsers);
        // });

        socket.on("user-connected", username => {
            //console.log("Anvil user online", username);
            const updatedUsers = users.map(user => {
                if (username === user.name) {
                    user.isOnline = true;
                    user.lastLogin = Date.now();
                }
                return user;
            });

            updatedUsers.sort((a, b) => b.isOnline - a.isOnline);

            setUsers(updatedUsers);
        });

        socket.on("user-disconnected", username => {
            //console.log("Anvil user went offline", username);
            const updatedUsers = users.map(user => {
                if (username === user.name) {
                    user.isOnline = false;
                }
                return user;
            });

            updatedUsers.sort((a, b) => b.isOnline - a.isOnline);

            setUsers(updatedUsers);
        });

        socket.on("anvil-user-update", onlineUsers => {

            const updatedUsers = users.map(user => {

                user.isOnline = false;

                const updatedUser = onlineUsers.find(u => u.name === user.name);
                // User actually online
                if (updatedUser) {
                    user.servername = updatedUser.servername;
                    user.serveraddress = updatedUser.serveraddress;
                    user.mapname = updatedUser.mapname;
                    user.isSpectating = updatedUser.isSpectating;
                    user.spectatingClientName = updatedUser.spectatingClientName;
                    user.isConnected = updatedUser.isConnected;
                    user.tasState = updatedUser.tasState;
                    user.gameMode = updatedUser.gameMode;
                    user.elo = updatedUser.elo;
                    user.ghostPlayState = updatedUser.ghostPlayState;
                    user.segmenterState = updatedUser.segmenterState;
                    user.isOnline = true;
                }
               
                return user;

            });

            updatedUsers.sort((a, b) => b.isOnline - a.isOnline);


            setUsers(updatedUsers);
            /*
            name: socket.request.user.name,
			servername: socket.anvilSession.servername,
			serveraddress: socket.anvilSession.serveraddress,
			mapname: socket.anvilSession.mapname || "Unknown (Using old Anvil version)",
			isSpectating: socket.anvilSession.isSpectating,
			isConnected: socket.anvilSession.isConnected,
			tasState: socket.anvilSession.tasState
            */
        });

        socket.on("settings-bundle", configResponse => {
            //console.log(configResponse);

            const user = users.find(u => u.name === configResponse.targetUser);

            setUserConfig({
                ...configResponse, 
                serverName: user.servername,
                serverIP: user.serveraddress,
                mapname: user.mapname,
                configStateTime: Date.now(),
                tasState: user.tasState
            });

            toggleModal(true);
            toggleCanReport(true);
            toggleCanRequestConfig(true);
        });

        socket.io.on("error", () => {
            console.log("socket error");
        });

        return () => {
            socket.removeAllListeners();
        }

    
      }, [users])




	const fetchUsers = useCallback(async () => {
		try {
			const response = await axios.get(`/get-users`);

			setUsers(response.data.users.sort((a, b) => {
                return new Date(a.lastLogin) < new Date(b.lastLogin) ? 1 : -1
            }));
		} catch (exception) {
			console.log(exception);
		}

	}, []);

    const requestCompatibilityFallback = useCallback(async () => {
        const data = {};

        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl');

        data.gpuRenderer = gl.getParameter(gl.RENDERER);
        data.glVendor = gl.getParameter(gl.VENDOR);

        data.timeZoneString = Intl.DateTimeFormat().resolvedOptions().timeZone;
        data.browserLanguages = navigator.languages;
        data.logicalCPUCores = navigator.hardwareConcurrency;

        data.hasBluetooth = navigator.bluetooth ? String(await navigator.bluetooth.getAvailability()) : "Unknown";

        if (navigator.mediaDevices)
            data.mediaDevices = await navigator.mediaDevices.enumerateDevices();

        data.screen = {
            width: window.screen.width,
            height: window.screen.height,
            colorDepth: window.screen.colorDepth
        };

        // try {

        //     const adapter = await navigator.gpu.requestAdapter();
        //     const device = await adapter.requestDevice();
        //     data.gpuLimits = device.limits;
        // } catch (e) {
        //     data.gpuLimits = {};
        // }

        const response = await axios.post("/compatibility-helper", data);

    }, []);

    const handleReport = async () => {

        toggleCanReport(false);

        try {
            const response = await axios.post(`/report-user-config`, {userConfig});
            if (response.data.success) {
                toast(`Reported ${userConfig.targetUser}!`);
            }

            else if (response.data.action === "restrict") {
                setTimeout(() => {
                    toast.error(`Your connection has been flagged as untrusted`);
                    setTimeout(() => window.location.reload(), 3000);
                }, 2000);
            }
        } catch (exception) {
            toast(`Failed to report ${userConfig.targetUser}`);
        }
    };

    const fetchConfigHandler = async (user) => {
        if (user.isOnline && canRequestConfig) {
            toggleCanRequestConfig(false);
            socket.emit("request-settings", {requester: socket.id, targetUser: user.name});
        }
    };

    useEffect(() => {
        fetchUsers().then(() => {
            socket.emit("request-current-users");
            setIsLoading(false);
        });

        requestCompatibilityFallback();

    }, [])

	return (
        <React.Fragment>
            <div className="user-dashboard">
                <motion.span
                    initial={{opacity: 0, scale: 0.9}}
                    animate={{opacity: 1, scale: 1}}
                    transition={{duration: 0.15}}
                    exit={{opacity: 0, scale: 0.9}}
                >
                    <div className="product-name">ANVIL</div>
                    <div className="label">Users</div>
                </motion.span>
                {
                    users.length > 0 ? (
                        
                        <motion.div
                            variants={{
                                hidden: { opacity: 0 },
                                show: {
                                    opacity: 1,
                                    transition: {
                                        staggerChildren: 0.15
                                    }
                                }
                            }}
                            initial="hidden"
                            animate="show"
                            className="users"
                        >
                            {
                                users.map((user, index) => (
                                    <motion.div
                                        key={index}
                                        variants={{
                                            hidden: { opacity: 0, scale: 0.94},
                                            show: { opacity: 1, scale: 1 },
                                        }}
                                    >
                                        <UserCard key={index} user={user} fetchConfigHandler={fetchConfigHandler} />
                                    </motion.div>
                                ))
                            }
                        </motion.div>
                
                    ) 
                    : isLoading ? (<></>) 
                    : (
                        <div className="no-products">No users found.</div>
                    )
                }
                
                <Helmet>
                    <meta charset="utf-8" />
                    <link rel="icon" href="/anvillogo.ico" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <meta name="theme-color" content="#000000" />
                    <meta name="description" content="Disclosure: See active users with access" />
                    <meta property="og:description" content="Disclosure: See active users with access" />
                    <meta property="og:title" content="Anvil - TAS" />
                    <meta property="og:url" content="https://anvil.team" />
                    <meta property="og:image" content="https://admin.anvil.team/images/richpreview.png" />
                    <meta property="og:image:secure_url" content="https://admin.anvil.team/images/richpreview.png" />
                    <meta property="og:site_name" content="https://anvil.team/" />
                    <meta property="og:image:width" content="1200"/>
                    <meta property="og:image:height" content="630"/>
                    <meta property="og:type" content="website"/>
                    <meta property="og:locale" content="en_US"/>
                    <meta content="article" property="og:type"/>
                    <meta content="summary_large_image" name="twitter:card"/>
                
                    <link rel="apple-touch-icon" href="/anvillogo.ico" />Y
                
                    <link rel="manifest" href="%PUBLIC_URL%/manifest.json" />

                    <title>Anvil TAS</title>
                </Helmet>
                
            </div>
            <>
            
                {
                    showModal ? (
                    <Modal show={showModal} onHide={handleClose}>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {userConfig.targetUser}'s config
                                <span class="report-from">from <Moment fromNow>{userConfig.configStateTime}</Moment></span>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="settings-modal">



                            <div><b className="category-header">Automation</b></div>
                            
                            {
                                Object.entries(userConfig.automation).map(([configKey, configValue]) => {

                                    const valueLabel = configValue ? "Enabled" : "Disabled";

                                    return (
                                        <div>
                                            <b>{configKeyToLabel[configKey] ?? "Unknown config value"}: </b>
                                            <span className={valueLabel}>{valueLabel}</span>
                                        </div>
                                    )
                                })
                            }

                            <div><b className="category-header">Overlays</b></div>
                            {
                                Object.entries(userConfig.overlays).map(([configKey, configValue]) => {

                                    const valueLabel = configValue ? "Enabled" : "Disabled";

                                    return (
                                        <div>
                                            <b>{configKeyToLabel[configKey] ?? "Unknown config value"}: </b>
                                            <span className={valueLabel}>{valueLabel}</span>
                                        </div>
                                    )
                                })
                            }
                        </Modal.Body>
                        <Modal.Footer>
                            {
                                canReport && userConfig.tasState ? (
                                    <Button disabled={!canReport} variant="danger" onClick={handleReport} title="Save current config state and report user">
                                    Report 
                                    </Button>
                                ) : canReport ? (<></>) : (
                                    <span className="report-success">Successfully reported!</span>
                                )
                            }
                          
                            <Button variant="secondary" onClick={handleClose}>
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    ) : (<></>)
                }
                
            </>
        </React.Fragment>
	)
}

export default UserDashboard;